import { type ComponentProps, memo, type ReactNode, useMemo } from 'react';

import { EllipsisHorizontalIcon, EllipsisVertical, Gear } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { NotificationsFilters, type NotificationsFiltersProps } from './NotificationsFilters';

export type NotificationsHeaderProps = ComponentProps<'header'> &
  NotificationsFiltersProps & {
    leading?: ReactNode;
    options?: DropdownMenuOption[];
    onMarkAllAsRead?: () => void;
    onSettingsClick?: () => void;
    isUI2?: boolean;
  };

export const NotificationsHeader = memo(
  ({
    filters,
    leading,
    onFilterClick,
    onMarkAllAsRead,
    onSettingsClick,
    options,
    isUI2,
    ...restOfProps
  }: NotificationsHeaderProps) => {
    const dropdownOptions = useMemo(
      () => [
        ...(onMarkAllAsRead
          ? [
              {
                id: 'mark-all-read',
                label: 'Mark all as read',
                onSelect: onMarkAllAsRead,
                type: 'item' as const,
              },
            ]
          : []),
        ...(onSettingsClick
          ? [
              {
                id: 'settings',
                label: 'Settings',
                onSelect: onSettingsClick,
                type: 'item' as const,
              },
            ]
          : []),
      ],
      [onMarkAllAsRead, onSettingsClick],
    );

    return (
      <header
        className="sticky top-0 z-0 shrink-0 border-b border-b-grey-4 bg-grey-1 pt-4"
        data-testid="NOTIFICATIONS_HEADER"
        {...restOfProps}
      >
        <div className="flex items-center justify-between px-4">
          {leading ? (
            <div className="flex items-center gap-2">
              {leading}
              <h3 className="font-semibold text-14 text-grey-12">Notifications</h3>
            </div>
          ) : (
            <h3 className="font-semibold text-14 text-grey-12">Notifications</h3>
          )}

          <div className="flex shrink-0 items-center gap-2">
            {isUI2 ? (
              <DropdownMenu
                align="end"
                trigger={<IconButton appearance="ghost" color="grey" icon={EllipsisHorizontalIcon} label="Settings" />}
              >
                {renderDropdownItems({
                  options: dropdownOptions,
                })}
              </DropdownMenu>
            ) : (
              <>
                {!!onSettingsClick && (
                  <IconButton
                    appearance="ghost"
                    color="grey"
                    icon={Gear}
                    label="Notification settings"
                    onClick={onSettingsClick}
                  />
                )}

                {!!options && (
                  <DropdownMenu
                    align="end"
                    trigger={<IconButton appearance="ghost" color="grey" icon={EllipsisVertical} label="Settings" />}
                  >
                    {renderDropdownItems({ options })}
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex min-h-[40px] items-center justify-between pr-4">
          <NotificationsFilters filters={filters} onFilterClick={onFilterClick} />

          {!isUI2 && (
            <Button appearance="ghost" className="shrink-0" color="grey" onClick={onMarkAllAsRead} size="small">
              Mark all as read
            </Button>
          )}
        </div>
      </header>
    );
  },
);

NotificationsHeader.displayName = 'NotificationsHeader';
